import { useCallback, useMemo } from "react";
import { useAtom } from "jotai";
import { ManagerOptions, SocketOptions } from "socket.io-client";
import { ConversationMakerSocket } from "@/core/socket";
import { SOCKET_HOST, SOCKET_PATH } from "@/core/variables";
import { User, WebSocketEventHandler, WebSocketEventType } from "@/model";
import { getClientId } from "@/util/stringUtil";
import { isSocketConnectedAtom, socketAtom, connectUsersAtom } from "@/atom/websocket";


export type ScenarioUploadOptionKey = "scenario" | "templates" | "constants" | "publisher" | "faq" | "properties";

export const useWebsocket = () => {
    const [socket, setSocket] = useAtom(socketAtom);
    const [isConnected, setIsConnected] = useAtom(isSocketConnectedAtom);
    const [connectUsers, setConnectUsers] = useAtom(connectUsersAtom);

    const connect = useCallback((connectScenarioId: string, loginUser: User, opts?: Partial<ManagerOptions & SocketOptions>) => {
        if (socket) {
            try {
                socket.disconnect();
            } catch (e) {
                setSocket(undefined);
                setIsConnected(false);
            }
        }

        const newSocket = new ConversationMakerSocket(`${SOCKET_HOST}`, `${SOCKET_PATH}`, opts);
        newSocket.connect(
            {
                client_id: getClientId(),
                scenario_id: connectScenarioId,
                email: loginUser.email,
                name: loginUser.name,
                uid: loginUser.user_id
            },
            () => {
                setIsConnected(true);
            }
        );

        newSocket.on("disconnected", () => {
            setIsConnected(false);
        });

        newSocket.on(
            "client_connected",
            (response: any) => {
                setConnectUsers(response.data.clients);
            }
        );

        newSocket.on(
            "client_disconnected",
            (response: any) => {
                setConnectUsers(response.data.clients);
            }
        );

        setSocket(newSocket);
    }, [socket, setSocket, setIsConnected, setConnectUsers]);

    const disconnect = useCallback(() => {
        if (!socket) {
            return;
        }
        try {
            socket.disconnect();
        } catch (e) { }
        setSocket(undefined);
        setIsConnected(false);
    }, [socket, setIsConnected, setSocket]);

    const addEventListener = useCallback((
        event: WebSocketEventType, callback: WebSocketEventHandler
    ) => {
        if (!socket) {
            return; 
        }
        socket.on(event, callback);
    }, [socket]);

    const removeEventListener = useCallback((
        event: WebSocketEventType, callback: WebSocketEventHandler
    ) => {
        if (!socket) {
            return;
        }
        socket.on(event, callback);
    }, [socket]);

    const clear = useCallback(() => {
        disconnect();
        setConnectUsers([]);
    }, [disconnect, setConnectUsers]);

    const requestBulkTest = useCallback((scenarioId: string, file: File, title: string) => {
        if (!socket) {
            throw new Error("Socket is no t connected");
        }
        socket.emit("request_bulk_test", {
            file,
            scenario_id: scenarioId,
            client_id: getClientId(),
            title: title,
            file_name: file.name
        });
    }, [socket]);

    const requestScenarioUpload = useCallback((
        scenarioId: string,
        file: File,
        option: {[key in ScenarioUploadOptionKey]: boolean
    }) => {
        if (!socket) {
            throw new Error("Socket is not connected");
        }
        socket.emit("request_scenario_import", {
            file,
            scenario_id: scenarioId,
            client_id: getClientId(),
            ...option
        });
    }, [socket]);

    return useMemo(() => {
        return {
            isConnected,
            connectUsers,
            connect,
            disconnect,
            addEventListener,
            requestBulkTest,
            requestScenarioUpload,
            removeEventListener,
            clear
        };
    }, [
        isConnected,
        connectUsers,
        connect,
        disconnect,
        addEventListener,
        requestBulkTest,
        requestScenarioUpload,
        removeEventListener,
        clear
    ]);
};
